import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Upload, Plus, Info } from 'lucide-react';
import { SearchInput, DataTable, FilterBar, PageShell } from '@/components/shared';
import { RegistrationsManagerViewProps } from './registrationsmanager.props';
import RegistrationsFormDialog from './registrationsmanager.form';
import {
  normalizeBibNumberingConfig,
} from '@/utils/bibNumbering';

function getRaceTypeTooltip(raceType: any): string {
  const numbering = normalizeBibNumberingConfig(raceType);
  if (!numbering.bibNumberingEnabled) {
    return 'Овој тип не добива стартни броеви.';
  }
  return 'Стартните броеви мора да бидат единствени само во овој тип на трка.';
}

interface RaceTypeFilterSelectProps {
  value: string;
  onChange: (v: string) => void;
  raceTypes: Record<string, any[]>;
  getRaceTypeDetails: (raceTypeId: any) => any;
}

function RaceTypeFilterSelect({ value, onChange, raceTypes, getRaceTypeDetails }: RaceTypeFilterSelectProps) {
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const allTypes: any[] = Object.values(raceTypes).flat();
  const uniqueTypes = allTypes.filter((t, i, arr) => arr.findIndex((x) => x.id === t.id) === i);

  const selectedDetails = value ? getRaceTypeDetails(value) : null;
  const tooltipText = selectedDetails ? getRaceTypeTooltip(selectedDetails) : null;

  return (
    <div className="flex items-center gap-1">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
      >
        <option value="">Сите типови</option>
        {uniqueTypes.map((type: any) => (
          <option key={type.id} value={type.id}>{type.name}</option>
        ))}
      </select>
      {tooltipText && (
        <div className="relative flex-shrink-0">
          <Info
            className="w-4 h-4 text-muted-foreground cursor-pointer"
            onMouseEnter={() => setTooltipVisible(true)}
            onMouseLeave={() => setTooltipVisible(false)}
          />
          {tooltipVisible && (
            <div className="absolute z-50 bottom-full right-0 mb-2 w-56 rounded-md bg-popover text-popover-foreground border border-border shadow-md px-3 py-2 text-xs">
              {tooltipText}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const RegistrationsManagerView: React.FC<RegistrationsManagerViewProps> = ({
  loading,
  stats,
  searchQuery,
  setSearchQuery,
  filterRace,
  setFilterRace,
  filterStatus,
  setFilterStatus,
  filterPayment,
  setFilterPayment,
  filterRaceType,
  setFilterRaceType,
  races,
  columns,
  filteredRegistrations,
  handleExportCSV,
  selectedRegistration,
  isDetailDialogOpen,
  setIsDetailDialogOpen,
  isConfirmDialogOpen,
  setIsConfirmDialogOpen,
  confirmingRegistration,
  handleConfirm,
  confirmRegistrationPending,
  deleteTarget,
  setDeleteTarget,
  confirmDelete,
  isBibDialogOpen,
  setIsBibDialogOpen,
  editingBibRegistration,
  bibNumberInput,
  setBibNumberInput,
  handleSaveBibNumber,
  updateBibNumberPending,
  getRaceName,
  getRaceTypeName,
  getRaceTypeDetails,
  getDiscountInfo,
  getFinalPrice,
  raceTypePrices,
  embedded,
  isEditDialogOpen,
  setIsEditDialogOpen,
  editingRegistration,
  registrationFormData,
  setRegistrationFormData,
  handleOpenCreate,
  handleCloseEditDialog,
  handleSubmitRegistration,
  raceTypes,
}) => {
  const navigate = useNavigate();
  return (
    <PageShell>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card><CardContent className="p-4"><div className="text-sm text-muted-foreground">Вкупно регистрации</div><div className="text-2xl font-bold text-foreground">{stats.total}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-sm text-muted-foreground">Потврдени</div><div className="text-2xl font-bold text-green-600">{stats.confirmed}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-sm text-muted-foreground">Во обработка</div><div className="text-2xl font-bold text-yellow-600">{stats.pending}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-sm text-muted-foreground">Платено</div><div className="text-2xl font-bold text-primary">{stats.paid}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-sm text-muted-foreground">Неплатено</div><div className="text-2xl font-bold text-red-600">{stats.unpaid}</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Управување со регистрации</CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate('/cms/registrations/import')}>
                <Upload className="w-4 h-4 mr-2" />Импортирај CSV
              </Button>
              <Button onClick={handleExportCSV}><Download className="w-4 h-4 mr-2" />Експортирај CSV</Button>
              <Button onClick={handleOpenCreate}><Plus className="w-4 h-4 mr-2" />Додај регистрација</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <FilterBar className={'grid grid-cols-1 ' + (embedded ? 'md:grid-cols-4' : 'md:grid-cols-5') + ' gap-4 mb-4'}>
            <SearchInput value={searchQuery} onChange={setSearchQuery} placeholder="Пребарувај по име, email или стартен број..." />
            {!embedded && (
              <select value={filterRace} onChange={(e) => setFilterRace(e.target.value)} className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
                <option value="">Сите настани</option>
                {races.map((race: any) => (<option key={race.id} value={race.id}>{race.name}</option>))}
              </select>
            )}
            <RaceTypeFilterSelect
              value={filterRaceType}
              onChange={setFilterRaceType}
              raceTypes={raceTypes}
              getRaceTypeDetails={getRaceTypeDetails}
            />
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
              <option value="">Сите статуси</option>
              <option value="Confirmed">Потврдени</option>
              <option value="Pending">Во обработка</option>
              <option value="Cancelled">Откажани</option>
            </select>
            <select value={filterPayment} onChange={(e) => setFilterPayment(e.target.value)} className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
              <option value="">Сите плаќања</option>
              <option value="Paid">Платено</option>
              <option value="Pending">Неплатено</option>
            </select>
          </FilterBar>
          <DataTable columns={columns} data={filteredRegistrations} loading={loading} emptyMessage="Нема регистрации што одговараат на филтрите." stickyHeader maxHeight="65vh" />
        </CardContent>
      </Card>

      <RegistrationsFormDialog
        isConfirmDialogOpen={isConfirmDialogOpen}
        setIsConfirmDialogOpen={setIsConfirmDialogOpen}
        confirmingRegistration={confirmingRegistration}
        handleConfirm={handleConfirm}
        confirmRegistrationPending={confirmRegistrationPending}
        isDetailDialogOpen={isDetailDialogOpen}
        setIsDetailDialogOpen={setIsDetailDialogOpen}
        selectedRegistration={selectedRegistration}
        isBibDialogOpen={isBibDialogOpen}
        setIsBibDialogOpen={setIsBibDialogOpen}
        editingBibRegistration={editingBibRegistration}
        bibNumberInput={bibNumberInput}
        setBibNumberInput={setBibNumberInput}
        handleSaveBibNumber={handleSaveBibNumber}
        updateBibNumberPending={updateBibNumberPending}
        getRaceName={getRaceName}
        getRaceTypeName={getRaceTypeName}
        getRaceTypeDetails={getRaceTypeDetails}
        getDiscountInfo={getDiscountInfo}
        getFinalPrice={getFinalPrice}
        raceTypePrices={raceTypePrices}
        deleteTarget={deleteTarget}
        setDeleteTarget={setDeleteTarget}
        confirmDelete={confirmDelete}
        isEditDialogOpen={isEditDialogOpen}
        setIsEditDialogOpen={setIsEditDialogOpen}
        editingRegistration={editingRegistration}
        registrationFormData={registrationFormData}
        setRegistrationFormData={setRegistrationFormData}
        handleSubmitRegistration={handleSubmitRegistration}
        handleCloseEditDialog={handleCloseEditDialog}
        races={races}
        raceTypes={raceTypes}
      />

    </PageShell>
  );
};

export default RegistrationsManagerView;
